import { ReactNode, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ModalsKeys, Nullable, SelectOption } from 'global';
import { CreditSubmission, Proposal } from '@trnsact/trnsact-shared-types';
import { ProposalProductsDialogModalData } from 'types';
import { NotifyAboutRemovedProductsDialogData } from 'modules/desking/types';
import {
  AftermarketSaveAsDialogData,
  AftermarketShareMenuConfirmationDialogData,
} from 'modules/aftermarketMenuConstructor/types';
import {
  FinanceFormModalData,
  PreviewFinanceFormModalData,
  QuoteProgramDialogData,
} from 'modules/financeProgram/types';
import { ApprovalTerms } from 'components/CompareApprovals/types';
import { ApprovalFormValues } from 'modules/creditSubmission/submissionDetails/types';
import { actions, modalsSelectors } from '../redux/modalsReducer';
import {
  AftermarketPartnerAddDealerDialogData,
  AftermarketPartnerAddLocationDialogData,
} from 'modules/aftermarket/partner/types';
import { CreateUpdateDialogData } from 'modules/invoice';
import {
  ProposalContractDetailsDialogData,
  ProposalCreateProductDialogData,
  ProposalMenuOptionDetailsDialogData,
} from 'modules/proposals/types';
import { CorporateGuarantorDialogData } from 'modules/creditApplicationDetailsActions/types';
import {
  ChargesEquipmentDialogData,
  CreateChargeDialogData,
  CreateUpdateEquipmentDialogData,
} from 'modules/equipment/types';
import { AddEditDeclineReasonDialogData, CreditSubmissionDetailsDialogData } from 'modules/creditSubmission/types';
import { IntegrationSettingsDialogData } from 'modules/integrationSettings/types';
import { ViewBroadcastDialogData } from 'modules/broadcasts/types';
import { SmsConversationData } from 'modules/dealActivity';
import { DealActivityLinkType } from '@trnsact/trnsact-shared-types';
import { AllDealActivity } from 'modules/dealActivity/api/types';
import type { DocumentNode } from '@apollo/client/core';

type ProposalModalsDataTypes = {
  [ModalsKeys.ProposalCreationDialog]: void;
  [ModalsKeys.ProposalSentDialog]: {
    onSent: (notification: { sendVia: string; ccEmails: SelectOption[] }) => Promise<void>;
    ccOptions: SelectOption[];
  };
  [ModalsKeys.ProposalProductsDialog]: ProposalProductsDialogModalData;
  [ModalsKeys.ProposalCreateProductDialog]: ProposalCreateProductDialogData;
  [ModalsKeys.ProposalContractDetailsDialog]: ProposalContractDetailsDialogData;
  [ModalsKeys.ProposalMenuOptionDetailsDialog]: ProposalMenuOptionDetailsDialogData;
};

type QuoteModalsDataTypes = {
  [ModalsKeys.SendInsuranceQuoteDialog]: any;
  [ModalsKeys.CreateAndSendQuote]: { voId: string };
  [ModalsKeys.QuoteProgramDialog]: QuoteProgramDialogData;
};

type AccountModalsDataTypes = {
  [ModalsKeys.AccountContactDialog]: {
    accountId: string;
    contactId?: string;
    customerInfo?: Record<string, any>;
  };
};

type CustomerModalsDataTypes = {
  [ModalsKeys.CustomerAccountDialog]: {};
};

export type ModalsDataTypes = {
  [ModalsKeys.Load]: any;
  [ModalsKeys.CreateEquipment]: any;
  [ModalsKeys.Confirmation]: {
    title: string;
    message: React.ReactNode;
    onConfirm?: VoidFunction;
    actionButtons?: ReactNode;
  };
  [ModalsKeys.FinanceForm]: FinanceFormModalData;
  [ModalsKeys.PreviewFinanceForm]: PreviewFinanceFormModalData;
  [ModalsKeys.DeskingConfirmSaveDraftDialog]: any;
  [ModalsKeys.ProductCatalogDialog]: any;
  [ModalsKeys.AftermarketAddEditMenuOptionDialog]: any;
  [ModalsKeys.AftermarketSaveAsDialog]: AftermarketSaveAsDialogData;
  [ModalsKeys.AftermarketShareMenuConfirmationDialog]: AftermarketShareMenuConfirmationDialogData;
  [ModalsKeys.AftermarketPartnerAddDealerDialog]: AftermarketPartnerAddDealerDialogData;
  [ModalsKeys.AftermarketPartnerAddLocationDialog]: AftermarketPartnerAddLocationDialogData;
  [ModalsKeys.AftermarketCreatePartnerDialog]: any;
  [ModalsKeys.PrescreenProgramsDialog]: any;
  [ModalsKeys.DeskingNotifyAboutRemovedProductsDialog]: NotifyAboutRemovedProductsDialogData;
  [ModalsKeys.ConstructorPricingInfoDialog]: any;
  [ModalsKeys.CreateUpdateInvoiceDialog]: CreateUpdateDialogData;
  [ModalsKeys.UploadInvoiceDialog]: { VOId: string; dynamicsVOId: string };
  [ModalsKeys.CorporateGuarantorDialog]: CorporateGuarantorDialogData;
  [ModalsKeys.CreateUpdateEquipmentDialog]: CreateUpdateEquipmentDialogData;
  [ModalsKeys.ChargesEquipmentDialog]: ChargesEquipmentDialogData;
  [ModalsKeys.CreateChargesEquipmentDialog]: CreateChargeDialogData;
  [ModalsKeys.InsuranceSelectSubmissionDialog]: any;
  [ModalsKeys.InsuranceEnterManualLenderRequirementsDialog]: any;
  [ModalsKeys.InsuranceAddEditBrokerDialog]: any;
  [ModalsKeys.AddEditDeclineReasonDialog]: AddEditDeclineReasonDialogData;
  [ModalsKeys.CreditSubmissionDetailsDialog]: CreditSubmissionDetailsDialogData;
  [ModalsKeys.IntegrationSettingsDialog]: IntegrationSettingsDialogData;
  [ModalsKeys.SyncDealInfoDialog]: any;
  [ModalsKeys.DocuSignDialog]: {
    vendorOpportunityId: string;
    link: Nullable<string>;
    creditSubmissionDynamicsId?: string;
  };
  [ModalsKeys.AddContingencyDialog]: Record<string, any>;

  [ModalsKeys.CallActivityDialog]: { parentId: string; parentType: DealActivityLinkType; entityId?: string };
  [ModalsKeys.NoteActivityDialog]: { parentId: string; parentType: DealActivityLinkType; entityId?: string };
  [ModalsKeys.ActivityDialog]: {
    parentId: string;
    parentType: DealActivityLinkType;
    entityId?: string;
    initialTab?: 'CALL' | 'NOTE';
  };
  [ModalsKeys.NewMessageDialog]: {
    voId: string;
    activityList: AllDealActivity[];
    onConfirm: (data: SmsConversationData) => void;
  };

  [ModalsKeys.UploadImagesForExistingEquipmentDialog]: { equipmentId: string };
  [ModalsKeys.PreviewExistingEquipmentImagesDialog]: { equipmentId: string };

  [ModalsKeys.UploadImagesForNewEquipmentDialog]: { onSave: (files: File[]) => void };
  [ModalsKeys.PreviewNewEquipmentImagesDialog]: { files: File[]; onConfirm: (files: File[]) => void };

  [ModalsKeys.ViewBroadcastDialog]: ViewBroadcastDialogData;
  [ModalsKeys.ExternalDashboardDialog]: { id?: string };
  [ModalsKeys.SwitchAccountDialog]: any;
  [ModalsKeys.SearchInventoryDialog]: { onSubmit: (listOfEquipmentId: string[]) => Promise<void> };
  [ModalsKeys.CalculatedRevenueDialog]: { vendorOpportunityId: string };

  [ModalsKeys.AddDealerDialog]: { refetchVPs: VoidFunction };
  [ModalsKeys.EditApprovalOverviewDialog]: {
    creditSubmission: CreditSubmission & { proposals?: Proposal[]; contingencies: string[] };
    approvalTerms: ApprovalTerms | undefined;
    docFeeTotal: number;
    parsedDecisionId: string | null;
    onSave: (approvalValues: ApprovalFormValues, conditions: string[]) => Promise<void>;
  };
  [ModalsKeys.DealSheetRuleDetailsDialog]: { configId: string; document: DocumentNode };
} & ProposalModalsDataTypes &
  QuoteModalsDataTypes &
  AccountModalsDataTypes &
  CustomerModalsDataTypes;

export function useModal<Key extends ModalsKeys>(modalKey: Key) {
  const dispatch = useDispatch();

  const isOpen = useSelector((state: any) => modalsSelectors.selectIsOpen(state, modalKey)) as boolean;
  const data = useSelector((state: any) => modalsSelectors.selectData(state, modalKey)) as ModalsDataTypes[Key];

  const handleOpen = useCallback(
    (data: ModalsDataTypes[Key] = null) => {
      dispatch(actions.onOpenModal(modalKey, data));
    },
    [dispatch, modalKey]
  );

  const handleClose = useCallback(() => {
    dispatch(actions.onCloseModal(modalKey));
  }, [dispatch, modalKey]);

  const handleUpdate = useCallback(
    (data: Nullable<Partial<ModalsDataTypes[Key]>> = null) => {
      dispatch(actions.onUpdate(modalKey, data));
    },
    [dispatch, modalKey]
  );

  return {
    data,
    isOpen,
    handleOpen,
    handleClose,
    handleUpdate,
  };
}
